import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	Card,
	Typography,
	Space,
	Button,
	Divider,
	Tooltip,
	Popover,
	DatePicker,
	Alert,
	Grid,
	Tag,
	Checkbox,
	message,
	Empty,
	Row,
	Input,
} from 'antd';
import {
	CopyOutlined,
	EditOutlined,
	CheckOutlined,
	QuestionCircleOutlined,
	InboxOutlined,
	PictureOutlined,
	VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// Kept: existing helpers/components (logic must remain intact)
import LoadingOverlay from '../../Overlays/LoadingOverlay';
import { useModeratorPost } from '../../../hooks/useModeratorPost';
import PostRegenerateEditor from './PostRegenerateEditor';
import { distActions } from '../../../store'; // only used for error emulation kept from original

// NOTE: We keep the original copy icons imports out to avoid breaking asset bundling.
import copyIcon from '../../../pages/icons/copy-icon.svg'; // kept (used inside <img/> to avoid regressions)
import penIcon from '../../../pages/icons/pen-icon.svg'; // kept
import checkIcon from '../../../pages/icons/check-icon-black.svg'; // kept
import CollaboratorsCard from '../../../components/Cards/CollaboratorsCard';
import InstagramContentTypeCard from '../../Cards/InstagramContentTypeCard';
import KonvaImageEditorModal from '../../modals/KonvaEditorModal';

/**
 * Visual-only redesign of PostContentEditorDetailsV2 using Ant Design components.
 * All logic, side-effects, and DOM ids are preserved. Tailwind classes removed/reduced where safe.
 */
const PostContentEditorDetailsV2 = ({ employeeId, autopublish }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch(); // kept for failure emulation
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md; // < md → mobile layout
	const autopublicationChecked = useSelector(
		(state) => state.dist.autopublishByEmployee[employeeId], // ✅ Uses the employeeId variable
	);
	const onToggleOne = useCallback(
		(employeeId, checked) => {
			dispatch(distActions.setEmployeeAutopublish({ employeeId, enabled: checked }));
		},
		[dispatch],
	);

	// ---------------- Redux state (kept) ----------------
	const employeeSocialMediaMap = useSelector((s) => s.dist.employeeSocialMediaMap);
	const connectedPlatforms = employeeSocialMediaMap?.[employeeId] || [];
	const jobStatus = useSelector((s) => s.dist.jobStatus[employeeId]);
	const jobState = jobStatus?.status; // queued | delayed | active | completed | failed
	const jobError = jobStatus?.error;
	const showOverlay = jobState === 'queued' || jobState === 'delayed' || jobState === 'active';
	const jobResult = useSelector((s) => s.dist.jobStatus[employeeId]?.result);
	const pastCollaborators = useSelector((state) => Object.values(state.dist.instagramAccounts));

	// ---------------- Hook with all business logic (kept) ----------------
	const {
		employee,
		currentPost,
		tempImages,
		tempVideo,
		isGenerating,
		initializePost,
		updatePostContent,
		addImages,
		removeImage,
		clearImages,
		setVideo,
		clearVideo,
		regeneratePost,
		submitPost,
		validatePost,
		toggleSocialNetwork,
		replaceImage,
	} = useModeratorPost(employeeId);

	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(true);

		// Check if this is a centralized image drag
		if (
			e.dataTransfer.types.includes('application/json') ||
			e.dataTransfer.types.includes('text/plain')
		) {
			try {
				// Only set the drop effect, don't parse the data here
				e.dataTransfer.dropEffect = 'copy';
			} catch (error) {
				// Silently fail during dragOver - parsing might not work here
			}
		}
	}, []);

	// Update handleDrop to properly handle the file transfer
	const handleDrop = useCallback(
		(e) => {
			e.preventDefault();
			e.stopPropagation();
			setDragActive(false);

			let dragData = null;

			// Try to get drag data
			if (e.dataTransfer.types.includes('application/json')) {
				try {
					dragData = JSON.parse(e.dataTransfer.getData('application/json'));
				} catch (error) {
					console.error('Error parsing JSON drag data: ', error);
				}
			}

			if (!dragData && e.dataTransfer.types.includes('text/plain')) {
				try {
					dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
				} catch (error) {
					console.error('Error parsing text drag data: ', error);
				}
			}

			// Handle generated image from AI Image Generator
			if (
				dragData &&
				dragData.type === 'generated-image' &&
				dragData.source === 'ai-image-generator'
			) {
				if (window.draggedFiles && window.draggedFiles['generated-image']) {
					const file = window.draggedFiles['generated-image'];
					addImages([file]);
					message.success(`Generated image added to ${employee.fullName}'s post`);
					delete window.draggedFiles['generated-image'];
					return;
				} else {
					message.error('Could not retrieve the generated image');
					return;
				}
			}

			let centralizedImageData = null;

			// First, try to get data from JSON
			if (e.dataTransfer.types.includes('application/json')) {
				try {
					centralizedImageData = JSON.parse(e.dataTransfer.getData('application/json'));
				} catch (error) {
					console.error('Error parsing JSON drag data: ', error);
				}
			}

			// If JSON failed, try text/plain as fallback
			if (!centralizedImageData && e.dataTransfer.types.includes('text/plain')) {
				try {
					centralizedImageData = JSON.parse(e.dataTransfer.getData('text/plain'));
				} catch (error) {
					console.error('Error parsing text drag data: ', error);
				}
			}

			// Handle centralized image drop
			if (
				centralizedImageData &&
				centralizedImageData.type === 'centralized-image' &&
				centralizedImageData.source === 'central-media-card'
			) {
				const fileIndex = centralizedImageData.fileIndex;

				// Retrieve the file from the temporary global storage
				if (window.draggedFiles && window.draggedFiles[`centralized-${fileIndex}`]) {
					const file = window.draggedFiles[`centralized-${fileIndex}`];
					addImages([file]);
					message.success(`Image added to ${employee.fullName}'s post`);

					// Clean up
					delete window.draggedFiles[`centralized-${fileIndex}`];
					return;
				} else {
					message.error('Could not retrieve the dragged image');
				}
			}

			// Handle regular file drops
			if (e.dataTransfer.files && e.dataTransfer.files[0]) {
				const list = Array.from(e.dataTransfer.files);
				const imageFiles = list.filter((f) => f.type.includes('image'));
				if (imageFiles.length > 0) {
					addImages(imageFiles);
					return;
				}
			}
		},
		[addImages, employee.fullName],
	);

	// Add a cleanup effect to handle drag end
	useEffect(() => {
		const handleDragEnd = () => {
			// Clean up any leftover dragged files
			if (window.draggedFiles) {
				window.draggedFiles = {};
			}
		};

		document.addEventListener('dragend', handleDragEnd);
		return () => {
			document.removeEventListener('dragend', handleDragEnd);
			// Cleanup on unmount
			if (window.draggedFiles) {
				window.draggedFiles = {};
			}
		};
	}, []);

	// ---------------- Local UI state (kept) ----------------
	const [publishTime, setPublishTime] = useState(Date.now());
	const [editMainText, setEditMainText] = useState(false);
	const [editHashtag, setEditHashtag] = useState(false);
	const [editOffer, setEditOffer] = useState(false);
	const [dragActive, setDragActive] = useState(false);
	const [collaborators, setCollaborators] = useState([]);
	const [collaboratorInput, setCollaboratorInput] = useState('');
	const [stories, setStories] = useState(false);
	const [postType, setPostType] = useState('Carousel');
	const [showImageEditor, setShowImageEditor] = useState(false);
	const [editingImageIndex, setEditingImageIndex] = useState(null);

	// Refs (kept)
	const hiddenVideoInput = useRef(null);
	const hiddenFileInput = useRef(null);
	const videoPlayerRef = useRef(null);

	// ---------------- Effects (kept) ----------------
	useEffect(() => {
		if (jobResult) {
			// Important: force new reference; logic unchanged
			initializePost({ ...jobResult });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(jobResult)]);

	if (!employee) return null;

	// ---------------- Social icon render (kept, but wrapped in antd) ----------------
	const socialIcons = [
		{ platform: 'LINKEDIN', label: 'LinkedIn', color: '#0a66c2' },
		{ platform: 'FACEBOOK', label: 'Facebook', color: '#1877f2' },
		{ platform: 'INSTAGRAM', label: 'Instagram', color: '#d6249f' },
		{ platform: 'THREADS', label: 'Threads', color: '#262626ff' },
	];
	const visibleSocialIcons = socialIcons.filter(({ platform }) =>
		connectedPlatforms.includes(platform),
	);

	const renderSocialIcon = ({ platform, label, color }) => {
		const isSelected =
			Array.isArray(currentPost.socialNetwork) && currentPost.socialNetwork.includes(platform);
		return (
			<Tag
				key={platform}
				color={isSelected ? 'success' : 'default'}
				style={{ cursor: 'pointer', userSelect: 'none' }}
				onClick={() => toggleSocialNetwork(platform)}
			>
				<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
					<span style={{ width: 8, height: 8, borderRadius: 99, background: color }} />
					{label}
				</span>
			</Tag>
		);
	};

	// ---------------- Event handlers (logic preserved) ----------------
	const changeMainText = () => {
		if (editMainText) {
			const text = document.querySelector(`#mainText-${employeeId}`).value;
			if (text.length > 2000) return message.error(t('functions.maxTextLength', { limit: 2000 }));
			if (text.trim().length === 0) return message.error(t('functions.noText'));
			if (text.trim().length < 25)
				return message.error(t('functions.minTextLength', { limit: 25 }));
			updatePostContent({ post: text });
		}
		setEditMainText((v) => !v);
	};

	const changeHashtags = () => {
		if (editHashtag) {
			const hashtags = document.querySelector(`#hashtags-${employeeId}`).value;
			updatePostContent({ hashtags });
		}
		setEditHashtag((v) => !v);
	};

	const changeOffer = () => {
		if (editOffer) {
			const media = document.querySelector(`#media-${employeeId}`).value;
			updatePostContent({ media: media.split(',').map((m) => m.trim()) });
		}
		setEditOffer((v) => !v);
	};

	const changePublishTime = (value) => {
		setPublishTime(value ? dayjs(value).valueOf() : Date.now());
		updatePostContent({ publishTime: value });
	};

	const clipboardCopy = (text) => {
		navigator.clipboard.writeText(text || '');
		message.success(t('functions.copied'));
	};

	// File handling (kept)
	const handleClick = () => {
		if (tempImages.length > 10) return message.error(t('functions.maxImages'));
		hiddenFileInput.current?.click();
	};
	const handleChange = (e) => {
		if (e.target.files && e.target.files.length > 0) addImages(e.target.files);
	};
	const handleDeleteImage = (index) => {
		if (window.confirm(t('post.removeSingleMediaConfirm'))) removeImage(index);
	};
	const handleResetImages = () => {
		if (window.confirm(t('post.removeAllMediaConfirm'))) clearImages();
	};
	const handleAddVideo = (e) => {
		const files = e?.target?.files;
		const file = files && files[0];
		if (!file) return;
		// Respect exclusivity (hook will also re-check)
		if (tempImages.length > 0) {
			message.error(t('postPage.media.videoBlockedByImages') || 'Remove images to attach a video.');
			return;
		}
		setVideo(file);
		// reset input so the same file can be re-selected later if removed
		if (hiddenVideoInput.current) hiddenVideoInput.current.value = '';
	};
	const handleRemoveVideo = () => {
		clearVideo();
		if (hiddenVideoInput.current) hiddenVideoInput.current.value = '';
	};
	const handleSaveEditedImage = async (editedFile) => {
		if (editingImageIndex === null) return;

		try {
			// Replace the image in tempImages array
			const updatedImages = [...tempImages];
			updatedImages[editingImageIndex] = editedFile;

			// Update the state - you'll need to check what method your hook provides
			// Option A: If your hook has a method to replace images
			if (typeof replaceImage === 'function') {
				await replaceImage(editingImageIndex, editedFile);
			}
			// Option B: If you need to clear and re-add (fallback)
			else {
				clearImages(); // Clear all images
				addImages(updatedImages); // Re-add all images including edited one
			}

			message.success('Image edited successfully');
		} catch (error) {
			console.error('Error saving edited image:', error);
			message.error('Failed to save edited image');
		} finally {
			setShowImageEditor(false);
			setEditingImageIndex(null);
		}
	};

	// DnD (kept)
	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(true);
	};
	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
	};

	// Failure emulation (kept)
	const emulateFailure = () => {
		dispatch(
			distActions.setJobStatus({
				employeeId,
				jobId: 'debug-job-id',
				status: 'failed',
				result:
					'⚠️ Генерация поста завершилась с ошибкой. Проверьте параметры запроса или попробуйте ещё раз.',
				error: 'Error happened because of the hand-triggered error emulation',
			}),
		);
	};

	// NEW: stories flag handler – persists into the per-employee post payload
	const onStoriesToggle = (e) => {
		const checked = !!e.target.checked;
		setStories(checked);
		updatePostContent({ stories: checked });
	};

	// Past collaborators mock

	// ---------------- Render ----------------
	return (
		<div className="relative">
			<LoadingOverlay loading={showOverlay} message={t('functions.generating')}>
				<Space direction="vertical" style={{ width: '100%' }} size={16}>
					{/* Error state */}
					{jobState === 'failed' && (
						<Alert
							type="error"
							showIcon
							message={t('functions.error') || 'Post generation failed'}
							description={
								<Space direction="vertical" style={{ width: '100%' }}>
									<Typography.Text>{jobError}</Typography.Text>
									<PostRegenerateEditor
										onConfirm={(customPrompt) => regeneratePost(customPrompt)}
										disabled={isGenerating || jobState === 'queued' || jobState === 'delayed'}
									/>
								</Space>
							}
						/>
					)}
					{/* Social networks */}
					<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
						<Space direction="vertical" style={{ width: '100%' }}>
							<Typography.Title level={5} style={{ margin: 0 }}>
								{t('postDistribution.cards.targetNetworks') || `Target networks`}
							</Typography.Title>
							{visibleSocialIcons.length === 0 && (
								<Alert type="warning" showIcon message={t('postDistribution.hint.noSocialMedia')} />
							)}
							<Space size={[8, 8]} wrap>
								{(visibleSocialIcons.length ? visibleSocialIcons : socialIcons).map(
									renderSocialIcon,
								)}
							</Space>
						</Space>
					</Card>
					{currentPost.socialNetwork.includes('INSTAGRAM') ? (
						<InstagramContentTypeCard postType={postType} setPostType={setPostType} />
					) : null}
					{/* Post Title / Text */}
					<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
						<Space direction="vertical" style={{ width: '100%' }} size={12}>
							<Typography.Title level={5} style={{ margin: 0 }}>{`${t(
								'postDistribution.contentEditor.postFor',
							)} ${employee.fullName}`}</Typography.Title>

							{/* Title (read-only block) */}
							<Space direction="vertical" style={{ width: '100%' }}>
								<Typography.Text strong>
									{t('postDistribution.contentEditor.postTitle')}
								</Typography.Text>
								<div
									style={{
										background: '#f4f0ff',
										borderRadius: 12,
										padding: 12,
										whiteSpace: 'pre-line',
									}}
								>
									{currentPost.postTitle}
								</div>
							</Space>

							<Divider style={{ margin: '8px 0' }} />

							{/* Main text */}
							<Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
								<Typography.Text strong>
									{t('postDistribution.contentEditor.postBody')}
								</Typography.Text>
								<Space>
									{/* Keep image icons to avoid asset changes */}
									<Tooltip title={t('postPage.buttons.copy')}>
										<img
											className="cursor-pointer"
											onClick={() => clipboardCopy(currentPost.post)}
											src={copyIcon}
											alt="copyicon"
											style={{ width: 18, height: 18 }}
										/>
									</Tooltip>
									<Tooltip
										title={editMainText ? t('postPage.buttons.save') : t('postPage.buttons.edit')}
									>
										<img
											className="cursor-pointer"
											onClick={changeMainText}
											src={editMainText ? checkIcon : penIcon}
											alt="editicon"
											style={{ width: 18, height: 18 }}
										/>
									</Tooltip>
								</Space>
							</Space>

							<Space align="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
								<Space size={6}>
									<Typography.Text type="secondary">
										{t('postDistribution.contentEditor.planned')}
									</Typography.Text>
									<DatePicker
										locale={{ dateTimeFormat: localStorage.getItem('ln') || 'en' }}
										disabledDate={(current) => current && current < moment().startOf('day')}
										defaultValue={dayjs(new Date(publishTime))}
										format="DD.MM.YYYY HH:mm"
										showTime
										onChange={(value) => value && changePublishTime(value.toISOString())}
									/>
								</Space>
							</Space>

							{editMainText ? (
								<div style={{ position: 'relative' }}>
									<textarea
										id={`mainText-${employeeId}`}
										defaultValue={currentPost.post}
										className="w-full p-3 rounded-lg"
										style={{ background: '#f4f0ff', height: 160, resize: 'none' }}
										placeholder="Enter post content..."
									/>
									<div
										style={{
											position: 'absolute',
											right: 8,
											bottom: -18,
											fontSize: 12,
											opacity: 0.8,
										}}
									>
										{currentPost.post?.length || 0} / 2000
									</div>
								</div>
							) : (
								<div
									style={{
										background: '#f4f0ff',
										borderRadius: 12,
										padding: 12,
										whiteSpace: 'pre-line',
									}}
								>
									{currentPost.post}
								</div>
							)}
						</Space>
					</Card>
					{/* Hashtags */}
					<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
						<Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
							<Typography.Title level={5} style={{ margin: 0 }}>
								{t('postDistribution.contentEditor.hashtags')}
							</Typography.Title>
							<Space>
								<Tooltip title={t('postPage.buttons.copy')}>
									<img
										className="cursor-pointer"
										onClick={() => clipboardCopy(currentPost.hashtags)}
										src={copyIcon}
										alt="copyicon"
										style={{ width: 18, height: 18 }}
									/>
								</Tooltip>
								<Tooltip
									title={editHashtag ? t('postPage.buttons.save') : t('postPage.buttons.edit')}
								>
									<img
										className="cursor-pointer"
										onClick={changeHashtags}
										src={editHashtag ? checkIcon : penIcon}
										alt="editicon"
										style={{ width: 18, height: 18 }}
									/>
								</Tooltip>
							</Space>
						</Space>
						<Divider style={{ margin: '12px 0' }} />
						{editHashtag ? (
							<textarea
								id={`hashtags-${employeeId}`}
								defaultValue={currentPost.hashtags}
								className="w-full p-3 rounded-lg"
								style={{ background: '#f4f0ff', height: 100, resize: 'none' }}
								placeholder="#marketing #b2b #casestudy"
							/>
						) : (
							<div style={{ background: '#f4f0ff', borderRadius: 12, padding: 12 }}>
								{currentPost.hashtags}
							</div>
						)}
					</Card>
					{/* Media suggestions */}
					<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
						<Space direction="vertical">
							<Typography.Title level={5} style={{ margin: 0 }}>
								{t('postDistribution.contentEditor.mediaSuggestion')}
							</Typography.Title>
							<Tooltip title={t('postPage.buttons.copy')}>
								<img
									className="cursor-pointer"
									onClick={() =>
										clipboardCopy(
											Array.isArray(currentPost.media)
												? currentPost.media.join(', ')
												: currentPost.media,
										)
									}
									src={copyIcon}
									alt="copyicon"
									style={{ width: 18, height: 18 }}
								/>
							</Tooltip>
							<Tooltip title={editOffer ? t('postPage.buttons.save') : t('postPage.buttons.edit')}>
								<img
									className="cursor-pointer"
									onClick={changeOffer}
									src={editOffer ? checkIcon : penIcon}
									alt="editicon"
									style={{ width: 18, height: 18 }}
								/>
							</Tooltip>
						</Space>

						<Divider style={{ margin: '12px 0' }} />
						{editOffer ? (
							<textarea
								id={`media-${employeeId}`}
								defaultValue={
									Array.isArray(currentPost.media)
										? currentPost.media.join(', ')
										: currentPost.media
								}
								className="w-full p-3 rounded-lg"
								style={{ background: '#f4f0ff', height: 120, resize: 'none' }}
								placeholder="Enter media suggestions..."
							/>
						) : (
							<div style={{ background: '#f4f0ff', borderRadius: 12, padding: 12 }}>
								{Array.isArray(currentPost.media)
									? currentPost.media.join(', ')
									: currentPost.media}
							</div>
						)}

						{/* Helper */}
						<Popover
							overlayStyle={{ maxWidth: 520 }}
							placement="bottom"
							content={
								<pre style={{ fontSize: 12 }} className="block whitespace-pre-wrap">
									Recommended image/video formats and platform-specific limits.
								</pre>
							}
							title="Media Requirements"
						>
							<div
								style={{
									display: 'flex',
									gap: 6,
									alignItems: 'center',
									justifyContent: 'center',
									marginTop: 12,
									cursor: 'help',
								}}
							>
								<QuestionCircleOutlined style={{ color: 'blueviolet' }} />
								<span>{t('postPage.misc.imageLabel') || 'Image'}</span>
							</div>
						</Popover>
					</Card>
					{/* Upload area */}
					<Card bordered className="shadow-sm" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
						{/* Header: title + actions; stacks on mobile, inline on desktop */}
						<Space
							direction={isMobile ? 'vertical' : 'horizontal'}
							style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'space-between' }}
							size={isMobile ? 8 : 12}
						>
							<Typography.Title level={5} style={{ margin: 0 }}>
								{t('postPage.cards.mediaTitle') || 'Add media'}
							</Typography.Title>

							{/* Actions area: wraps and becomes full-width buttons on mobile */}
							<Space wrap size={8} style={{ width: isMobile ? '100%' : 'auto' }}>
								{tempImages.length > 0 && (
									<Button
										danger
										onClick={handleResetImages}
										block={isMobile}
										size={isMobile ? 'middle' : 'default'}
									>
										{t('postPage.buttons.removeImages')}
									</Button>
								)}

								<Button
									icon={<PictureOutlined />}
									onClick={handleClick}
									disabled={!!tempVideo} // block images if a video is present
									block={isMobile}
									size={isMobile ? 'middle' : 'default'}
								>
									{t('postPage.buttons.uploadImage') || 'Upload image'}
								</Button>

								{tempVideo ? (
									<Button
										danger
										icon={<VideoCameraOutlined />}
										onClick={handleRemoveVideo}
										block={isMobile}
										size={isMobile ? 'middle' : 'default'}
									>
										{t('postPage.buttons.removeVideo') || 'Remove video'}
									</Button>
								) : (
									<Tooltip
										title={
											tempImages.length
												? t('postPage.media.videoBlockedByImages') ||
												  'Remove images to attach a video.'
												: undefined
										}
									>
										<Button
											icon={<VideoCameraOutlined />}
											disabled={tempImages.length > 0}
											onClick={() => hiddenVideoInput.current?.click()}
											block={isMobile}
											size={isMobile ? 'middle' : 'default'}
										>
											{t('postPage.buttons.addVideoReels') || 'Add video'}
										</Button>
									</Tooltip>
								)}
							</Space>
						</Space>

						<Divider style={{ margin: isMobile ? '10px 0' : '12px 0' }} />

						{/* Dropzone / preview area */}
						<div
							onDragEnter={handleDragEnter}
							onDragLeave={handleDragLeave}
							onDragOver={handleDragOver}
							onDrop={handleDrop}
							onClick={tempImages.length ? null : handleClick}
							style={{
								border: tempImages.length
									? '1px solid #f0f0f0'
									: `2px dashed ${dragActive ? '#722ed1' : '#d9d9d9'}`,
								borderRadius: 12,
								padding: isMobile ? 12 : 16,
								background: dragActive ? '#f9f0ff' : '#fafafa',
								cursor: 'pointer',
								minHeight: isMobile ? 80 : 96,
							}}
						>
							{tempImages.length ? (
								<Space direction="vertical" style={{ width: '100%' }} size={12}>
									{tempImages.map((image, index) => (
										<Card size="small" key={index} bodyStyle={{ padding: isMobile ? 10 : 12 }}>
											<div style={{ position: 'relative' }}>
												<img
													alt="preview"
													src={URL.createObjectURL(image)}
													style={{
														maxHeight: isMobile ? 160 : 200,
														width: '100%',
														objectFit: 'contain',
														borderRadius: 8,
													}}
												/>
												{/* ADD EDIT BUTTON */}
												<Button
													type="primary"
													size="small"
													style={{
														position: 'absolute',
														top: 8,
														right: 8,
													}}
													onClick={() => {
														setEditingImageIndex(index);
														setShowImageEditor(true);
													}}
												>
													<EditOutlined />
												</Button>
											</div>
											<Space
												align="center"
												style={{
													width: '100%',
													justifyContent: 'space-between',
													marginTop: 8,
													gap: 8,
													flexWrap: 'wrap',
												}}
											>
												<Typography.Text
													type={(image.size / 1024).toFixed() > 10000 ? 'danger' : 'secondary'}
												>
													{(image.size / 1024).toFixed(2)} KB
												</Typography.Text>
												<Button
													danger
													size={isMobile ? 'small' : 'small'}
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteImage(index);
													}}
												>
													{t('postDistribution.cards.removeSingleImage')}
												</Button>
											</Space>
										</Card>
									))}
								</Space>
							) : (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description={
										dragActive
											? t('postPage.dropzone.dropFilesToUpload')
											: t('postPage.dropzone.dragDropOrClick')
									}
								/>
							)}

							{tempVideo && (
								<div style={{ marginTop: 16 }}>
									{/* Make video fully responsive on mobile */}
									<video
										className="mx-auto"
										ref={videoPlayerRef}
										style={{ width: '100%', height: 'auto', maxWidth: isMobile ? '100%' : 520 }}
										controls
									>
										<source src={URL.createObjectURL(tempVideo)} type="video/mp4" />
									</video>
									<Typography.Paragraph
										style={{ textAlign: 'center', marginTop: 8 }}
										type={(tempVideo.size / 1_000_000).toFixed() > 200 ? 'danger' : 'secondary'}
									>
										{(tempVideo.size / 1_000_000).toFixed(2)} MB
									</Typography.Paragraph>
								</div>
							)}
						</div>
					</Card>
					{/* Autopublication */}
					<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
						<Space direction="vertical" size={12} style={{ width: '100%' }}>
							<Typography.Title level={5} style={{ margin: 0 }}>
								{t('postDistribution.contentEditor.autopublish')}
							</Typography.Title>
							<Checkbox
								checked={autopublicationChecked}
								onChange={(e) => onToggleOne(employee.id, e.target.checked)}
							/>
						</Space>
					</Card>
					{/* Actions */}

					<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
						{isGenerating ? (
							<Space direction="vertical" style={{ width: '100%', alignItems: 'center' }}>
								<div
									className="inline-block animate-spin rounded-full h-8 w-8 border-b-2"
									style={{ borderColor: '#722ed1' }}
								/>
								<Typography.Text type="secondary">Processing image…</Typography.Text>
							</Space>
						) : (
							<Space style={{ width: '100%', justifyContent: 'center' }} wrap>
								{jobStatus ? (
									<PostRegenerateEditor
										onConfirm={(customPrompt) => regeneratePost(customPrompt)}
									/>
								) : null}
								<Button
									type="primary"
									size="large"
									disabled={
										isGenerating || currentPost.sent || currentPost.socialNetwork.length === 0
									}
									onClick={() => submitPost(!!autopublish, postType)}
								>
									{isGenerating
										? `${t('postDistribution.contentEditor.sending')}`
										: `${t('postDistribution.contentEditor.sendTo')} ${employee.fullName}`}
								</Button>
							</Space>
						)}

						{currentPost.sent && (
							<Typography.Paragraph style={{ textAlign: 'center', marginTop: 8 }} type="success">
								✅ {t('postDistribution.contentEditor.sent')}
							</Typography.Paragraph>
						)}
					</Card>
					{/* Hidden inputs (kept) */}
					<input
						type="file"
						accept="image/*"
						onChange={handleChange}
						ref={hiddenFileInput}
						multiple
						style={{ display: 'none' }}
					/>
					<input
						onChange={handleAddVideo}
						ref={hiddenVideoInput}
						type="file"
						accept="video/mp4, video/mov, video/wmv, video/avi, video/flv, video/mpeg, video/mpg, video/quicktime, video/3gpp, video/webm"
						style={{ display: 'none' }}
					/>
				</Space>
			</LoadingOverlay>
			<KonvaImageEditorModal
				visible={showImageEditor}
				imageUrl={
					editingImageIndex !== null ? URL.createObjectURL(tempImages[editingImageIndex]) : null
				}
				onSave={handleSaveEditedImage}
				onCancel={() => {
					setShowImageEditor(false);
					setEditingImageIndex(null);
				}}
			/>
		</div>
	);
};

export default PostContentEditorDetailsV2;
